import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Avatar,
  Badge,
  Button,
  Flex,
  LoadingOverlay,
  ScrollArea,
  TextInput,
} from "@mantine/core";
import type { BadgeProps } from "@mantine/core";

import {
  getCompanyPhoto,
  updateCompanyById,
  uploadCompanyPhoto,
} from "../api/companyApi";
import { useAuth } from "../context/AuthContext";
import {
  errorNotification,
  saveNotification,
  updateNotification,
} from "../utils/useNotification";
import { unwrap } from "../utils/apiResult";

import Container from "../components/common/Container";
import PageHeader from "../components/common/PageHeader";
import { useForm } from "@mantine/form";
import { validateRequired } from "../utils/validations";
import { getLocalTimeString } from "../utils/dateFormats";
import CustomSearchInput from "../components/common/Inputs/CustomSearchInput";
import PlusIcon from "../components/common/Icons/PlusIcon";
import DocumentsInput from "../components/common/Inputs/DocumentsInput";
import CompanyLocationPicker from "../components/common/CompanyLocationPicker";

const GeneralSettings = () => {
  const { company, setCompany } = useAuth();

  const formRef = useRef<HTMLFormElement>(null);

  const [photos, setPhotos] = useState<{ [key: string]: File | null }>({
    hr: null,
    mobile: null,
    terminal: null,
  });
  const [previews, setPreviews] = useState<{ [key: string]: string | null }>({
    hr: null,
    mobile: null,
    terminal: null,
  });
  const [images, setImages] = useState<{ [key: string]: string | null }>({
    hr: null,
    mobile: null,
    terminal: null,
  });
  const [loadingState, setLoadingState] = useState<{ [key: string]: boolean }>({
    hr: true,
    mobile: true,
    terminal: true,
  });
  const [overlayVisible, setOverlayVisible] = useState<{
    [key: string]: boolean;
  }>({
    hr: false,
    mobile: false,
    terminal: false,
  });

  const form = useForm({
    initialValues: {
      name: company?.name as any,
      documents: company?.documents,
      latitude: company?.latitude ?? null,
      longitude: company?.longitude ?? null,
      geofenceRadius: company?.geofenceRadius ?? 300,
    },
    validate: {
      name: validateRequired,
    },
  });

  const uploadAllImages = async () => {
    try {
      setLoadingState((prev) => ({
        ...prev,
        hr: !!photos.hr,
        mobile: !!photos.mobile,
        terminal: !!photos.terminal,
      }));

      const uploadedPhotos = { ...images };
      const updatedCompany: any = { ...company };

      for (const type of Object.keys(photos)) {
        const photo = photos[type];
        if (!photo) continue;

        const formData = new FormData();
        formData.append("companyId", company?._id || "");
        formData.append("file", photo);

        try {
          const { data }: any = await uploadCompanyPhoto(formData, type);
          const fileId = data?.fileId;
          if (fileId) {
            switch (type) {
              case "mobile":
                updatedCompany.mobilePhoto = fileId;
                break;
              case "hr":
                updatedCompany.hrPhoto = fileId;
                break;
              case "terminal":
                updatedCompany.terminalPhoto = fileId;
                break;
              default:
                break;
            }
            uploadedPhotos[type] = URL.createObjectURL(photo);
          }
        } catch (error) {
          console.error(`Upload error for ${type}:`, error);
        }
      }

      setCompany(updatedCompany);
      setImages(uploadedPhotos);
      setPhotos({ hr: null, mobile: null, terminal: null });
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      setLoadingState({ hr: false, mobile: false, terminal: false });
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      setPhotos((prev) => ({ ...prev, [type]: file }));
      const previewURL = URL.createObjectURL(file);
      setPreviews((prev) => ({ ...prev, [type]: previewURL }));
    }
  };

  const getPhoto = async (photoId: string | null, type: string) => {
    try {
      if (!photoId) return;
      setLoadingState((prev) => ({ ...prev, [type]: true }));
      const response: any = await getCompanyPhoto(photoId, type);
      const imgUrl = URL.createObjectURL(response.data);
      setImages((prev) => ({ ...prev, [type]: imgUrl }));
    } catch (error) {
      console.error(`Fetch error for ${type}:`, error);
    } finally {
      setLoadingState((prev) => ({ ...prev, [type]: false }));
    }
  };

  useEffect(() => {
    getPhoto(company?.hrPhoto, "hr");
    getPhoto(company?.mobilePhoto, "mobile");
    getPhoto(company?.terminalPhoto, "terminal");
    // eslint-disable-next-line
  }, [company]);

  const updateCompany = async (updates: any) => {
    try {
      const result = unwrap(await updateCompanyById({ updates: updates }));
      if (result.ok) {
        setCompany((prev: any) => ({
          ...prev,
          ...updates,
        }));
        updateNotification(true);
      } else {
        errorNotification({ message: result.message ?? undefined });
      }
    } catch (error) {
      console.error("Error:", error);
      errorNotification();
    } finally {
    }
  };

  useEffect(() => {
    if (company) {
      form.setValues({
        name: company.name,
        latitude: company.latitude ?? null,
        longitude: company.longitude ?? null,
        geofenceRadius: company.geofenceRadius ?? 300,
      });
    }
  }, [company]);

  const handleSubmit = async (values: any) => {
    const updatedValues: any = { ...values };

    // Konum alanları yalnızca BLE modülü açıkken düzenlenebiliyor; kapalıyken
    // formda hiç görünmediği için gönderilmez de.
    if (company?.modules?.isBleAvailable) {
      updatedValues.latitude = values.latitude;
      updatedValues.longitude = values.longitude;
      updatedValues.geofenceRadius = values.geofenceRadius;
    } else {
      delete updatedValues.latitude;
      delete updatedValues.longitude;
      delete updatedValues.geofenceRadius;
    }
    await uploadAllImages();
    await updateCompany(updatedValues);
    setPreviews({
      hr: null,
      mobile: null,
      terminal: null,
    });
    form.resetDirty();
  };

  return (
    <Container>
      <PageHeader
        pageTitle="Genel Ayarlar"
        isButtoned
        buttonText="Kaydet"
        buttonIcon={<PlusIcon />}
        buttonOnClick={() => formRef.current?.requestSubmit()}
      />
      <Wrapper>
        <form
          ref={formRef}
          onSubmit={form.onSubmit(handleSubmit)}
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            gap: "40px",
          }}
        >
          {/*
            Üç bölüm (logolar · şirket adı · belgeler) dar ekranda yan yana
            sığmıyordu; mah=260px de dikeye inince içeriği kırpıyordu.
            Kırılım noktaları index.tsx'te theme.ts ile hizalı.
          */}
          <Flex
            align="start"
            mah={{ base: "none", md: "260px" }}
            gap={{ base: 24, md: 40 }}
            direction={{ base: "column", md: "row" }}
            w="100%"
          >
            <Flex
              flex={2}
              w="100%"
              style={{
                height: "-webkit-fill-available",
              }}
              gap={{ base: 20, md: 40 }}
              wrap={{ base: "wrap", md: "nowrap" }}
            >
              {["hr", "mobile", "terminal"].map((type) => (
                <Flex
                  direction="column"
                  align="start"
                  justify="start"
                  pos="relative"
                  key={type}
                  w={"100%"}
                >
                  <InputLabel>
                    {type === "hr"
                      ? "Şirket Logosu Yükle"
                      : type === "mobile"
                      ? "Mobil Logo Yükle"
                      : type === "terminal"
                      ? "Terminal Logo Yükle"
                      : ""}
                  </InputLabel>
                  <FileInput
                    id={`${type}-file-upload`}
                    type="file"
                    onChange={(e) => handleFileChange(e, type)}
                  />
                  <FileLabel
                    htmlFor={`${type}-file-upload`}
                    onMouseEnter={() =>
                      setOverlayVisible((prev) => ({ ...prev, [type]: true }))
                    }
                    onMouseLeave={() =>
                      setOverlayVisible((prev) => ({ ...prev, [type]: false }))
                    }
                  >
                    <LoadingOverlay
                      visible={loadingState[type]}
                      zIndex={1000}
                      overlayProps={{ radius: "sm", blur: 2 }}
                    />
                    {previews[type] || images[type] ? (
                      <PhotoPreview
                        src={previews[type] || images[type] || ""}
                        alt={`${type} preview`}
                        height={"100%"}
                        width={"100%"}
                        style={{ objectFit: "contain", borderRadius: "8px" }}
                        $isChanged={photos[type] && true}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "10px",
                          border: "2px dashed #5C26FF",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          boxShadow: "0px 0px 20px 0px #00000026",
                        }}
                      >
                        <Badge
                          bg={"#5C26FF"}
                          style={{
                            cursor: "pointer",
                          }}
                          circle
                          size="40"
                        >
                          <Avatar src={"/assets/plusIcon.png"} size={28} />
                        </Badge>
                      </div>
                    )}
                    {(overlayVisible[type] &&
                      (images[type] || previews[type])) ||
                    photos[type] ? (
                      <UploadIcon circle>
                        <Avatar
                          src={"/assets/uploadIcon.png"}
                          size={24}
                          radius={0}
                        />
                      </UploadIcon>
                    ) : null}
                  </FileLabel>
                </Flex>
              ))}
            </Flex>
            <Flex flex={1} w="100%" direction="column" justify={"space-between"}>
              <CustomSearchInput
                label="Şirket İsmi"
                leftSection={false}
                w="100%"
                {...form.getInputProps("name")}
              />
              {/* "Giriş/Çıkış Toleransı" alanları KALDIRILDI: `workHours` ile
                  aynı durumdaydılar — kaydediliyor ama ölçüt zincirinde hiçbir
                  yerde okunmuyorlardı, İK uygulanmayan bir politika girdiğini
                  sanıyordu. Geç/erken toleransı vardiya tanımında:
                  Vardiyalar → Vardiya Listesi → "Geç Kalma Toleransı (dk)". */}
            </Flex>
            {company?.modules?.isDocumentUploadAvailable && (
              <Flex flex={1} w="100%">
                <DocumentsInput form={form} />
              </Flex>
            )}
          </Flex>
          {/* Şirket geneli mesai saati KALDIRILDI (plan 2.30): ölçüt zinciri
              bu alanı hiç okumuyordu, tek tüketicisi bir dashboard satırıydı.
              Şirket geneli çalışma düzeni artık departmansız bir vardiya
              tanımıyla kurulur (Vardiyalar → Vardiya Listesi, plan 2.24). */}

          {/* Konum/geofence yalnızca mobil uygulamadaki konum bazlı aktif olma
              özelliği için kullanılıyor, o da BLE modülüne bağlı. BLE kapalıysa
              bu ayarın karşılığı yok, gösterilmiyor. */}
          {company?.modules?.isBleAvailable && (
            <Flex flex={1} w="100%">
              <CompanyLocationPicker
                latitude={form.values.latitude}
                longitude={form.values.longitude}
                geofenceRadius={form.values.geofenceRadius}
                onChange={(lat, lng, radius) => {
                  form.setFieldValue("latitude", lat);
                  form.setFieldValue("longitude", lng);
                  form.setFieldValue("geofenceRadius", radius);
                }}
              />
            </Flex>
          )}

          {/* 
            <StyledButton
              fullWidth
              disabled={
                !form.isDirty() &&
                !previews["hr"] &&
                !previews["mobile"] &&
                !previews["terminal"]
              }
              type="submit"
            >
              Kaydet
            </StyledButton> */}
        </form>
      </Wrapper>
    </Container>
  );
};

export default GeneralSettings;
const Wrapper = styled(ScrollArea)`
  display: flex;
  flex-direction: column;
  background-color: white;
`;

const InputLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  line-height: 17.64px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
`;

const FileInput = styled.input`
  display: none;
`;

const FileLabel = styled.label`
    width: 100%;
    height: 100%;
    position: relative;
    display: inline-block;
    margin-top: 10px;
    cursor: pointer;
    }
  `;

const PhotoPreview = styled.img<{ $isChanged: any }>`
  border: 2px solid
    ${(props): any => (props.$isChanged ? "#5C26FF" : "#d9d9d9")};
  border-radius: 8px;
  transition: border-color 0.3s ease;
  &:hover {
    box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.15);
  }
`;

const UploadIcon = styled(Badge)<BadgeProps>`
  position: absolute;
  width: 40px;
  height: 40px;
  top: -20px;
  right: -20px;
  background: #5c26ff;
  color: white;
  font-size: 20px;
  font-weight: bold;
  z-index: 10;
  cursor: pointer;
`;
